import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useQuery } from "@tanstack/react-query";
import { getListCorporateDocument } from "actions/RFQ/Supplier/rfqActions";
import {
	SmartFieldQuestionnaireBase,
	filterSmartFieldOptionForSubmittedRFQ,
} from "./SmartFieldQuestionnaireBase";
import { useSmartFieldSelection } from "../hooks/useSmartFieldSelection";
import { CorporateDocument } from "pages/RFQ/RFQ/CreateRFQ/PreviewRFQ/Questionnaire/_components/CorporateDocument";
import { filterDocumentsByAllowedCategories } from "pages/RFQSubmission/RFQPreview/RFQUtils/RFQHelper";
import { useDocumentCategories } from "newui/store";
import { isNotNullOrUndefined } from "newui/utils/objectUtil";
import { useRfxSubmissionData } from "newui/features/Rfx/data-provider/RfxSubmissionDataProvider";

const documentDummyData = {
	id: 0,
	file_name: "Example Document Name.pdf",
	category: {
		name: "",
	},
	size: 1000,
};

export const SmartFieldCorporateDocument = ({
	className,
	question,
	required,
	isClientPreview,
	answer,
	...props
}) => {
	const [search, setSearch] = useState("");
	const [selectedFilter, setSelectedFilter] = useState([]);
	const [filterParams, setFilterParams] = useState({});
	const { rfxId, isClientView } = useRfxSubmissionData();
	const companyId = useSelector((state) => state?.search?.activeCompany?.id);
	const { value, error, touched, isSelected, selectHandler } =
		useSmartFieldSelection(props);
	const { isLoading: categoriesLoading, values: documentCategories } =
		useDocumentCategories();

	// extract questionId from props.name
	const questionId = props.name?.split("-")[1];

	// Get allow_document_categories from props
	useEffect(() => {
		if (!props.fieldValue?.allow_document_categories) return;

		// Extract question ID from props.name (e.g., "question_answer.question-1927" -> "1927")
		const questionId = props.name?.split("-")[1];

		const params = {};
		if (questionId) {
			params[`filters[question_id]`] = questionId;
		}

		props.fieldValue.allow_document_categories.forEach((item, index) => {
			if (item.category_id) {
				params[`filters[category][${index}]`] = item.category_id;
			}
			if (item.subcategory_id) {
				params[`filters[category][${index}]`] = item.subcategory_id;
			}
		});

		setFilterParams(params);
	}, [props.name, props.fieldValue?.allow_document_categories]);

	const queryParams = {
		search,
		...filterParams,
	};

	const useAnswerData =
		(props?.disabled && answer?.selectedDocuments) || !rfxId || isClientView;

	// Get documents
	const {
		data,
		isLoading,
		error: queryError,
	} = useQuery({
		queryKey: ["corporateDocument", queryParams],
		queryFn: getListCorporateDocument,
		gcTime: 10 * 60 * 1000,
		enabled: Object.keys(filterParams).length > 0 && !useAnswerData,
	});

	// if the corporate_document is loaded already, use that (as we may have just transitioned from edit to view)
	const documents = data?.corporate_document ?? answer?.selectedDocuments ?? [];
	const filteringList = data?.filters || [];

	// filter by allowed category ids
	const allowedCategoryIds = (
		props.fieldValue?.allow_document_categories || []
	).map((item) => Number(item.category_id));

	const categoryFilteredDocuments = filterDocumentsByAllowedCategories(
		documents,
		allowedCategoryIds
	);

	const filteredData = filterSmartFieldOptionForSubmittedRFQ(
		categoryFilteredDocuments,
		value,
		props?.disabled
	);

	if (isClientPreview) {
		const firstAllowedId = allowedCategoryIds?.[0];
		const categoryName = isNotNullOrUndefined(firstAllowedId)
			? documentCategories.find((dc) => dc.value === firstAllowedId)?.label
			: null;
		filteredData.push({
			...documentDummyData,
			category: { name: categoryName ?? "Chosen Category shown here" },
		});
	}

	return (
		<SmartFieldQuestionnaireBase
			className={className}
			question={question}
			required={required}
			description="Please choose the corporate documents that you would like to share."
			searchPlaceholder="Search documents"
			showFilter={false}
			showSearch={!props?.disabled}
			isLoading={isLoading || categoriesLoading}
			filteringList={filteringList}
			selectedFilter={selectedFilter}
			setSelectedFilter={setSelectedFilter}
			setSearch={setSearch}
			error={error}
			touched={touched}
			isClientPreview={isClientPreview}
			{...props}
		>
			{queryError ? (
				<div className="text-red-500">
					Error loading documents: {queryError.message || "Unknown error"}
				</div>
			) : filteredData?.length > 0 ? (
				<CorporateDocument
					key={`corporate-doc-${questionId}`}
					values={filteredData.map((item) => ({
						...item,
						key: item.id || item.uuid,
					}))}
					selectHandler={selectHandler}
					selected={isSelected}
					isLoading={isLoading || categoriesLoading}
					companyId={companyId}
					isPreview={props?.disabled}
					linkStoragePath
					questionId={questionId}
					setFieldValue={props.setFieldValue}
				/>
			) : (
				<div className="italic text-gray-500">
					No documents {useAnswerData ? "selected" : "found"}
				</div>
			)}
		</SmartFieldQuestionnaireBase>
	);
};
